import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Form, Modal } from 'react-bootstrap'
import './AddContactGroupStyle.css'
import '../ModalStyle.css'
import { Button, InputField } from '@components/common'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useGlobalLoadingActions } from '@hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import {
  ContactGroupChannel,
  IContact,
  IContactGroup,
} from '@dataTypes/contact.type'
import { getFullName } from '@utils/fullname.util'
import { addContactGroup, updateConversation } from '@store/actions'
import { TGroupChanges } from '../ManageContactGroup/ManageContactGroupModal.component'
import {
  SET_CONTACT_LIST,
  SET_CONVERSATION_FOR_NEW_GROUP,
} from '@store/reducers/messageCenter.reducer'

type AddContactGroupProps = {
  initialData?: IContactGroup | null
  show: boolean
  onHide: () => void
  showAllContacts?: boolean
  onCancel?: () => void
  onSave?: () => void
  finalGroupData?: TGroupChanges
  setFinalGroupData?: Dispatch<SetStateAction<TGroupChanges>>
}

const ADD_GROUP_MODAL_LOADING_CONTAINER = '.modal.show .modal-content'

const defaultGroupData: IContactGroup = {
  clinic_id: '',
  group_name: '',
  channel: '',
  created_by: '',
  updated_by: '',
}

const AddContactGroupModal = ({
  initialData = null,
  show,
  onHide,
  showAllContacts = false,
  onCancel,
  onSave,
  finalGroupData,
  setFinalGroupData,
}: AddContactGroupProps): React.ReactElement => {
  const dispatch = useAppDispatch()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const contactDropDownRef = useRef<HTMLUListElement>(null)
  const contacts = useAppSelector((state) => state.contacts.data)
  const contactGroups = useAppSelector((state) => state.contactGroup.data)
  const channelState = useAppSelector((state) => state.messageCenter.channel)
  const { contactList: contactListState, conversationForNewGroup } =
    useAppSelector((state) => state.messageCenter)
  const [groupData, setGroupData] = useState<IContactGroup>(
    initialData ? { ...defaultGroupData, ...initialData } : defaultGroupData,
  )
  const [selectedContacts, setSelectedContacts] = useState<IContact[]>(
    initialData?.contacts && initialData?.contacts.length > 0
      ? initialData?.contacts
      : contactListState,
  )
  const [contactList, setContactList] = useState<IContact[]>([])
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string
  }>({})

  const handleClose = (): void => {
    dispatch(SET_CONVERSATION_FOR_NEW_GROUP(null))
    dispatch(SET_CONTACT_LIST([]))
    onHide()
  }

  const handlePlatformChange = (args: ChangeEvent<HTMLSelectElement>): void => {
    const value = args.target.value
    setGroupData({ ...groupData, channel: value })
    if (value.length > 0) {
      const { channel, ...otherErrors } = validationErrors
      setValidationErrors(otherErrors)
    } else {
      setValidationErrors({
        ...validationErrors,
        channel: 'This field is required',
      })
    }
  }

  const handleContactSelect = (contact: IContact): void => {
    setSelectedContacts([...selectedContacts, contact])
    setSearchQuery('')
  }

  const validateForm = (): { [key: string]: string } => {
    const requiredField: (keyof IContactGroup)[] = ['group_name', 'channel']
    const errors: { [key: string]: string } = {}

    requiredField.forEach((field) => {
      if (!groupData[field]) {
        errors[field] = 'This field is required'
      }
    })

    if (contacts.length > 0) {
      const index = contactGroups.findIndex(
        (group) => group.group_name === groupData.group_name,
      )
      if (index !== -1) {
        errors.group_name = 'Group name already exists'
      }
    }

    setValidationErrors(errors)
    return errors
  }

  const validateGroupName = (name: string): void => {
    if (name.length === 0) {
      setValidationErrors({
        ...validationErrors,
        group_name: 'Group name is required',
      })
    } else {
      const existing =
        contactGroups.length > 0
          ? contactGroups.find((c) => c.group_name === name)
          : undefined

      if (
        !existing ||
        initialData?.contact_group_id === existing.contact_group_id
      ) {
        const { group_name, ...otherErrors } = validationErrors
        setValidationErrors(otherErrors)
      } else {
        setValidationErrors({
          ...validationErrors,
          group_name: 'Group name already exists',
        })
      }
    }
  }

  const handleInputChange = (args: ChangeEvent<HTMLInputElement>): void => {
    const value = args.target.value
    setGroupData({ ...groupData, group_name: value })
    validateGroupName(value)
  }

  const handleSearchChange = (args: ChangeEvent<HTMLInputElement>): void => {
    const value = args.target.value
    setSearchQuery(value)
  }

  const handleCreate = async (): Promise<void> => {
    if (initialData) {
      onSave?.()
      handleClose()
      return
    }
    const errors = validateForm()
    if (Object.keys(errors).length > 0) return
    try {
      showLoading({ container: ADD_GROUP_MODAL_LOADING_CONTAINER })
      await dispatch(
        addContactGroup({ group: groupData, members: selectedContacts }),
      ).unwrap()
      if (conversationForNewGroup) {
        await dispatch(
          updateConversation({
            id: conversationForNewGroup,
            data: {
              name: groupData.group_name,
            },
          }),
        ).unwrap()
        dispatch(SET_CONVERSATION_FOR_NEW_GROUP(null))
      }
      showToast(
        createSuccessToast(
          'Group created',
          'Contact group has been created successfully.',
        ),
      )
      handleClose()
    } catch {
      showToast(
        createFailedToast(
          'Create group failed',
          'Failed to create contact group. Please try again.',
        ),
      )
    } finally {
      hideLoading(ADD_GROUP_MODAL_LOADING_CONTAINER)
    }
  }

  const handleCancel = (): void => {
    if (initialData) {
      onCancel?.()
    } else {
      handleClose()
    }
  }

  const handleRemoveMember = (contact: IContact): void => {
    setSelectedContacts(
      selectedContacts.filter((sc) => sc.contact_id !== contact.contact_id),
    )
  }

  useEffect(() => {
    if (initialData && setFinalGroupData) {
      const add =
        selectedContacts.length > 0
          ? selectedContacts.filter((sc) => {
              return !initialData.contacts?.some(
                (m) => m.contact_id === sc.contact_id,
              )
            })
          : null

      const remove =
        initialData.contacts && initialData.contacts.length > 0
          ? initialData.contacts.filter((m) => {
              return !selectedContacts?.some(
                (sc) => sc.contact_id === m.contact_id,
              )
            })
          : null

      const name =
        initialData.group_name === groupData.group_name
          ? null
          : groupData.group_name

      const channel =
        initialData.channel === groupData.channel ? null : groupData.channel

      setFinalGroupData({
        name,
        channel,
        add: add && add.length > 0 ? add : null,
        remove: remove && remove.length > 0 ? remove : null,
      })
    }
  }, [selectedContacts, initialData, groupData.group_name, groupData.channel])

  useEffect(() => {
    const filteredContacts =
      contacts.length > 0 && searchQuery.length > 0
        ? contacts.filter((c) => {
            let isChannelCompatible = false
            if (groupData.channel === '') isChannelCompatible = true
            if (groupData.channel === 'SMS' && c.cellphone)
              isChannelCompatible = true
            if (
              ['Messenger', 'Instagram'].includes(groupData.channel) &&
              c.patient?.meta_id
            )
              isChannelCompatible = true

            const index = c.contact_id
              ? selectedContacts.findIndex((s) => s.contact_id === c.contact_id)
              : -1
            return (
              getFullName(c)
                .toLowerCase()
                .includes(searchQuery.toLowerCase()) &&
              index === -1 &&
              isChannelCompatible
            )
          })
        : []

    setContactList(filteredContacts)
  }, [groupData.channel, searchQuery, selectedContacts])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (
        contactDropDownRef.current &&
        !contactDropDownRef.current.contains(e.target as Node)
      ) {
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (contactListState.length)
      setGroupData({ ...groupData, channel: channelState })
  }, [contactListState])

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      className="add-contact-group-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>{initialData ? 'Edit ' : 'Create '} group</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="acgm-details">
          <InputField
            label="Group name"
            placeholder="Charting priority"
            value={groupData.group_name}
            onChange={handleInputChange}
            hint={validationErrors.group_name || undefined}
            destructive={!!validationErrors.group_name}
            className={
              validationErrors.group_name
                ? 'select-field-destructive'
                : undefined
            }
          />
          <div className="acgm-platform-container">
            <label>Platform</label>
            <Form.Select
              onChange={handlePlatformChange}
              disabled={selectedContacts.length > 0}
              className={
                validationErrors.channel
                  ? 'select-field-destructive'
                  : undefined
              }
              value={groupData.channel}
            >
              <option key="empty-channel" value="">
                {' '}
              </option>
              {ContactGroupChannel.map((channel) => (
                <option key={channel.key} value={channel.value}>
                  {channel.value}
                </option>
              ))}
            </Form.Select>
            {validationErrors.channel && (
              <div className="text-danger">{validationErrors.channel}</div>
            )}
          </div>
        </div>
        <>
          <div className="acgm-input-contact">
            <InputField
              placeholder="Add contact name"
              leftIcon={<span className="mgc_search_2_line" />}
              value={searchQuery}
              onChange={handleSearchChange}
              disabled={groupData.channel.length === 0}
            />
            {contactList.length > 0 && (
              <ul className="acgm-contact-list" ref={contactDropDownRef}>
                {contactList.map((contact) => (
                  <li
                    key={contact.patient_id}
                    onClick={() => handleContactSelect(contact)}
                    className="contact-dropdown-item"
                  >
                    {getFullName(contact, true)}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {showAllContacts && (
            <div className="acgm-ac-container-secondary">
              {selectedContacts.length > 0 &&
                selectedContacts.map((c) => (
                  <div key={c.contact_id} className="added-contact">
                    {getFullName(c, true)}
                    <button
                      type="button"
                      className="unstyled-button mgc_close_line"
                      aria-label="Remove contact"
                      onClick={() => handleRemoveMember(c)}
                    />
                  </div>
                ))}
            </div>
          )}
        </>
        {selectedContacts.length > 0 && !showAllContacts && (
          <div className="acgm-member-list">
            <label>Added</label>
            <div className="acgm-ac-container">
              {selectedContacts.map((c) => (
                <div key={c.contact_id} className="added-contact">
                  {getFullName(c, true)}
                  <i className="mgc_close_line" />
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button className="button-secondary me-1" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          className="button-primary me-1"
          onClick={handleCreate}
          disabled={
            initialData && finalGroupData
              ? !(
                  finalGroupData.add ||
                  finalGroupData.remove ||
                  finalGroupData.name
                )
              : false
          }
        >
          {initialData ? 'Save' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AddContactGroupModal
